import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Card, Chip, DataTable, EmptyState, Icon, Modal, Pane, PaneHead, TabBar, type TableRow } from '@/components/kit';
import { useQueryClient } from '@tanstack/react-query';
import { useBranches } from '@/pages/roles/clinic-manager/lib/useManagerData';
import { flowsApi, type BookingType, type FlowStepDef, type FlowTemplate } from '../shared/useFlows';
import type { FlowItem } from './useLibraryApi';
import { ROLE_LABEL, ROLE_CHIP, box, InlineSelect, InlineEdit, useIsClinicVoice, useRoleLabel, PhoneFootnote, PhoneRowList } from './libraryBits';
import { useNarrowViewport } from '@/components/kit/useNarrow';

/**
 * Library ▸ Flows (`g_libflow`, f11b) — the team's choreography, as data.
 *
 * A FLOW is a named chain of steps, each owned by a role ("nurse → technician
 * → you"), some of them timed ("wait 20 min" — the step comes back to the next
 * person when it runs out). Written here; started from a visit (the
 * encounter's Flow block); worked from Today's Unfinished and each role's own
 * lists. Every template edit goes through `PATCH /flows/templates/:id`
 * (template:write); a running flow keeps the steps it started with.
 *
 * A STEP IS PICKED FROM ITEMS, not typed. That is what makes "change an item
 * once and every flow using it updates" true, and it is why the add-step
 * control opens a real picker of the clinic's vocabulary rather than an empty
 * text box. Free text is still allowed — a chain sometimes needs a word the
 * vocabulary has not got yet.
 *
 * THE AVAILABILITY FORK IS PER BRANCH. A step this branch cannot perform
 * resolves to EXTERNAL: a dispatched request, the episode PENDING, the return
 * in reception's book-list.
 */

/** "nurse → technician → you" in the Library; "… → doctor" under Settings (pass the reader's labels). */
export const executors = (steps: FlowStepDef[], labels: Record<FlowStepDef['assigneeRole'], string> = ROLE_LABEL) =>
  steps.map((s) => (labels[s.assigneeRole] ?? s.assigneeRole).toLowerCase()).join(' → ');

export function flowRows(
  templates: FlowTemplate[],
  onEdit: (id: string) => void,
  labels: Record<FlowStepDef['assigneeRole'], string> = ROLE_LABEL,
): TableRow[] {
  return templates.map((t) => ({
    id: t.id,
    cells: {
      name: (
        <>
          <span className="t-strong">{t.name}</span>{' '}
          <button type="button" className="dchip dchip-brand"
            onClick={(e) => { e.stopPropagation(); onEdit(t.id); }}>
            ✎ Edit
          </button>
          {t.isActive === false ? <> <Chip kind="outline" small>hidden</Chip></> : null}
        </>
      ),
      exec: <span className="t-sec mut">{executors(t.steps, labels) || '—'}</span>,
      steps: <span>{t.steps.length}</span>,
      used: <span>{t.usageCount}×</span>,
    },
  }));
}

export function FlowsMain({ templates, loading, failed, selectedId, onSelect, onEdit, tab, onTab, query, tabsOnly, note, pageSize = 3, onNew }: {
  templates: FlowTemplate[];
  loading: boolean;
  failed?: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  tab: 'flows' | 'items';
  onTab: (t: 'flows' | 'items') => void;
  query: string;
  /** Items renders the same tab row above its own list — see the Library page. */
  tabsOnly?: boolean;
  /** What sits under the list. The Library's and Settings' boards each write their own. */
  note?: ReactNode;
  /** How many rows before "Load more" — the Library's board draws 3, Settings' draws 4. */
  pageSize?: number;
  /** The phone's "+ New flow" — it sits at the foot of the list, not in the bar. */
  onNew?: () => void;
}) {
  const labels = useRoleLabel();
  const phone = useNarrowViewport();
  const q = query.trim().toLowerCase();
  const [shownCount, setShownCount] = useState(pageSize);
  const matching = q
    ? templates.filter((t) => `${t.name} ${executors(t.steps, labels)} ${t.steps.map((s) => s.label).join(' ')}`.toLowerCase().includes(q))
    : templates;
  const shown = matching.slice(0, shownCount);
  const month = new Date().toLocaleDateString('en-GB', { month: 'short' }).slice(0, 3);

  /* g_l12 — on a phone the two are chips, not tabs. */
  const tabs = phone ? (
    <div style={{ display: 'flex', gap: 8, margin: '0 0 12px' }}>
      {([['flows', 'Flows'], ['items', 'Items']] as const).map(([k, l]) => (
        <Chip key={k} kind={tab === k ? 'soft' : 'outline'} className={tab === k ? 'w6' : undefined}
          onClick={() => onTab(k)}>{l}</Chip>
      ))}
    </div>
  ) : (
    <div style={{ margin: '0 0 12px' }}>
      <TabBar
        items={[{ key: 'flows' as const, label: 'Flows' }, { key: 'items' as const, label: 'Items' }]}
        active={tab}
        onSelect={onTab}
        label="Flows"
      />
    </div>
  );
  if (tabsOnly) return tabs;

  if (phone) {
    const spoken = (t: FlowTemplate) => t.steps
      .map((x) => { const l = labels[x.assigneeRole] ?? x.assigneeRole; return l === 'You' ? 'you' : l; }).join(' → ');
    return (
      <>
        {tabs}
        <PhoneRowList
          rows={matching.map((t) => ({
            id: t.id,
            selected: t.id === selectedId,
            title: t.name,
            trailing: (
              <button type="button" className="dchip" style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
                onClick={(e) => { e.stopPropagation(); onEdit(t.id); }}>Edit</button>
            ),
            lines: `${spoken(t) || '—'} · ${t.steps.length} step${t.steps.length === 1 ? '' : 's'}`,
            onOpen: () => onSelect(t.id),
          }))}
          empty={(
            <EmptyState
              title={failed ? 'The flows did not load' : loading ? 'Loading…' : q ? `No flow matches “${query}”` : 'No flows yet'}
              sub={failed ? 'The read did not come back. Reload the page to try again.'
                : '+ New flow writes the first one — a chain of steps, each owned by a role.'}
            />
          )}
          foot={(
            <>
              {onNew ? (
                <p style={{ padding: '4px 4px 0' }}>
                  <button type="button" className="lnk w6" onClick={onNew}>+ New flow</button>
                </p>
              ) : null}
              <PhoneFootnote>Seeded by your specialty pack · edited by <b>doctors &amp; the clinic manager</b></PhoneFootnote>
            </>
          )}
        />
      </>
    );
  }

  return (
    <>
      {tabs}
      <DataTable
        columns={[
          { key: 'name', label: 'Flow', width: 46 },
          { key: 'exec', label: 'Executors', width: 26 },
          { key: 'steps', label: 'Steps', width: 10 },
          { key: 'used', label: `Used · ${month}`, width: 18 },
        ]}
        rows={flowRows(shown, onEdit, labels)}
        selectedId={selectedId}
        onSelect={(r) => onSelect(r.id)}
        total={matching.length}
        onLoadMore={shown.length < matching.length ? () => setShownCount((n) => n + 20) : undefined}
        caption="Flows"
        empty={
          <EmptyState
            title={failed ? 'The flows did not load'
              : loading ? 'Loading…'
              : q ? `No flow matches “${query}”` : 'No flows yet'}
            sub={failed
              ? 'The read did not come back. Reload the page to try again.'
              : '+ New flow writes the first one — a chain of steps, each owned by a role.'}
          />
        }
      />
      {note ?? (
        <>
          <p className="t-cap mut" style={{ padding: '12px 0 0' }}>
            Pack-seeded at signup · doctors and the clinic manager edit · executors execute · every edit
            logged. One editor, two entrances — the manager reaches this same screen from Settings.
          </p>
          <Card style={{ borderColor: 'var(--stroke-200)' }}>
            <p className="t-sec" style={{ color: 'var(--ink-600)' }}>
              The availability fork is per branch: no OCT here resolves step 2 to EXTERNAL — a
              dispatched request, the episode PENDING, the return in reception’s book-list.
            </p>
          </Card>
        </>
      )}
    </>
  );
}

/**
 * THE EDITOR — its own component with its own state, so the shell re-publishing
 * the pane never resets what the doctor is typing. Keyed by the flow's id.
 */
export function FlowEditorPane({ template, items, onSaved }: {
  template: FlowTemplate | null;
  items: FlowItem[];
  onSaved: (t: FlowTemplate) => void;
}) {
  const qc = useQueryClient();
  const labels = useRoleLabel();
  const clinicVoice = useIsClinicVoice();
  const [name, setName] = useState(template?.name ?? '');
  const [steps, setSteps] = useState<FlowStepDef[]>(() => template?.steps.map((s) => ({ ...s })) ?? []);
  const [adding, setAdding] = useState(false);
  const [trigger, setTrigger] = useState<BookingType | null>(template?.triggerBookingType ?? null);
  const [saving, setSaving] = useState(false);
  /** Which step's line is open — the board's card is one line until asked. */
  const [open, setOpen] = useState<number | null>(null);

  const valid = name.trim().length >= 2 && steps.length > 0 && steps.every((s) => s.label.trim());

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        steps: steps.map((s) => ({
          ...s,
          label: s.label.trim(),
          durationMin: s.durationMin || null,
          note: s.note?.trim() || null,
        })),
        triggerBookingType: trigger,
      };
      const saved = template ? await flowsApi.updateTemplate(template.id, body) : await flowsApi.createTemplate(body);
      await qc.invalidateQueries({ queryKey: ['flows'] });
      toast.success(template ? 'Flow saved' : 'Flow created');
      onSaved(saved);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The flow did not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Pane
      head={
        <PaneHead
          overline="THE EDITOR"
          title={
            <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Flow name"
              placeholder="Dilated fundus exam" className="t-title dr-titlefield" />
          }
          sub={`${executors(steps, labels) || '—'} · ${steps.length} step${steps.length === 1 ? '' : 's'}`}
        />
      }
      foot={
        <button type="button" className="btn btn-primary" style={{ flex: 1, minHeight: 44 }}
          disabled={!valid || saving} onClick={() => void save()}>
          {saving ? 'Saving…' : template ? 'Save flow' : 'Create flow'}
        </button>
      }
    >
      {/* A STEP CARD IS WHAT THE STEP CARRIES. The board draws the name, who
          does it, and the chips that are SET — nothing else. The ways to add a
          wait, a fork or a note, and the way to remove the step, live behind
          the step's own name, so an empty control never sits on a card that
          has not got that thing. */}
      {trigger ? <Card><TriggerPicker value={trigger} onChange={setTrigger} /></Card> : null}
      <StepEditor steps={steps} setSteps={setSteps} items={items}
        adding={adding} setAdding={setAdding} open={open} setOpen={setOpen} />
      <p className="t-cap mut">
        {clinicVoice
          ? 'the patient returns to the doctor’s READY lane when every “now” step finishes'
          : 'the patient returns to your READY lane when all “now” steps finish'}
      </p>
    </Pane>
  );
}

const BOOKING_LABEL: Record<BookingType, string> = {
  routine: 'a routine booking',
  'follow-up': 'a follow-up booking',
  urgent: 'an urgent booking',
  emergency: 'an emergency booking',
};

/**
 * TRIGGERED FROM — the encounter's Flow button (one tap), or every booking of
 * a type. One flow, one entrance: picking a booking type moves the entrance,
 * it does not add a second one.
 */
function TriggerPicker({ value, onChange }: { value: BookingType | null; onChange: (v: BookingType | null) => void }) {
  return (
    <>
      <p className="mt8" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" aria-pressed={!value} onClick={() => onChange(null)}
          style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}>
          <Chip kind={value ? 'outline' : 'soft'} small className="w6">the encounter — one tap</Chip>
        </button>
        <span className="chip-pill-select" style={{ display: 'inline-flex' }}>
          <InlineSelect value={value ?? ''} aria-label="A booking type"
            onChange={(v) => onChange((v || null) as BookingType | null)}
            options={[{ value: '', label: 'a booking type' },
              ...(Object.keys(BOOKING_LABEL) as BookingType[]).map((t) => ({ value: t, label: BOOKING_LABEL[t] }))]} />
        </span>
      </p>
      <p className="t-cap mut mt8">
        {value
          ? `one flow, one entrance — it starts with every ${BOOKING_LABEL[value].replace(/^an? /, '')}`
          : 'one flow, one entrance — the encounter’s Flow button starts it, or every booking of a type'}
      </p>
    </>
  );
}

/**
 * THE STEPS, IN ORDER — shared by the docked editor (f11b) and the New-flow
 * modal (f11b-new). One chain grammar, one place: a step written in the modal
 * and a step written in the pane must be the same kind of thing, or "change an
 * item once and every flow using it updates" stops being true.
 */
function StepEditor({ steps, setSteps, items, adding, setAdding, open, setOpen }: {
  steps: FlowStepDef[];
  setSteps: React.Dispatch<React.SetStateAction<FlowStepDef[]>>;
  items: FlowItem[];
  adding: boolean;
  setAdding: (v: boolean) => void;
  open: number | null;
  setOpen: (v: number | null) => void;
}) {
  const labels = useRoleLabel();
  const setStep = (i: number, patch: Partial<FlowStepDef>) =>
    setSteps((xs) => xs.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addFromItem = (id: string) => {
    const it = items.find((x) => x.id === id);
    setAdding(false);
    if (!it) return;
    setSteps((xs) => [...xs, {
      label: it.name,
      assigneeRole: it.assigneeRole,
      durationMin: it.waitMin ?? null,
      itemId: it.id,
      note: it.description ?? null,
      ifUnavailable: null,
    }]);
  };
  /* A FREE-TEXT STEP IS BORN NAMELESS, so the line that names it opens with
     it — a card reading "name this step" with the field hidden behind its own
     name is a dead end for anyone who does not know to click it. */
  const addFreeText = () => {
    setAdding(false);
    setSteps((xs) => {
      setOpen(xs.length);
      return [...xs, { label: '', assigneeRole: 'doctor', durationMin: null }];
    });
  };
  return (
    <>
    {steps.map((s, i) => {
      const mine = s.assigneeRole === 'doctor';
      const chips = !mine || s.durationMin || s.ifUnavailable;
      return (
        <Card key={i} style={mine ? undefined : { borderColor: 'var(--brand-300)' }}>
          <div className="flex" style={{ gap: 8 }}>
            <p className="t-strong grow dr-stepname" role="button" tabIndex={0}
              onClick={() => setOpen(open === i ? null : i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(open === i ? null : i); }
              }}>
              {i + 1} · {s.label || <span className="mut">name this step</span>}
            </p>
            <InlineSelect value={s.assigneeRole} aria-label={`Who does step ${i + 1}`}
              chevron={!mine}
              className={`chip chip-${ROLE_CHIP[s.assigneeRole]} chip-sm`}
              onChange={(v) => setStep(i, { assigneeRole: v as FlowStepDef['assigneeRole'] })}
              options={(Object.keys(labels) as Array<keyof typeof labels>)
                .map((r) => ({ value: r, label: labels[r] }))} />
          </div>

          {chips ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
              {/* YOUR OWN STEP IS NOT A NOW-TASK — the patient comes back to
                  your READY lane when the executors' "now" steps finish. */}
              {!mine ? <Chip kind="soft" small className="w6">now</Chip> : null}
              {!mine && i > 0 ? <Chip kind="outline" small>needs step {i} ✓</Chip> : null}
              {s.durationMin ? (
                <span className="chip chip-outline chip-sm">
                  <InlineEdit value={String(s.durationMin)} label={`Wait after step ${i + 1}, minutes`}
                    numeric mark="none" fieldStyle={{ width: 56 }}
                    onCommit={(v) => setStep(i, { durationMin: v && Number(v) > 0 ? Number(v) : null })}
                    display={<><Icon name="clock" size="sm" /> wait {s.durationMin} min <Icon name="pencil" size="sm" /></>} />
                </span>
              ) : null}
              {/* THE AVAILABILITY FORK — a real picker, per the board's amber chip. */}
              {s.ifUnavailable ? (
                <InlineSelect value={s.ifUnavailable} aria-label={`If step ${i + 1} is unavailable`}
                  className="chip chip-warn chip-sm"
                  onChange={(v) => setStep(i, { ifUnavailable: (v || null) as FlowStepDef['ifUnavailable'] })}
                  options={[
                    { value: 'external', label: 'if unavailable: external' },
                    { value: 'skip', label: 'if unavailable: skip it' },
                    { value: '', label: 'if unavailable: wait for us' },
                  ]} />
              ) : null}
            </div>
          ) : null}

          {/* The step's own note — the board's "protocol: Tropicamide +
              Phenylephrine". It comes from the Item when the step was picked
              from one, and stays editable here. */}
          {s.note !== null && s.note !== undefined ? (
            <p className="t-cap mut mt8">
              <InlineEdit value={s.note} onCommit={(v) => setStep(i, { note: v })} mark="none"
                label={`Note on step ${i + 1}`} placeholder="protocol: …" />
            </p>
          ) : null}

          {open === i ? (
            <div className="dr-stepextras">
              <input value={s.label} onChange={(e) => setStep(i, { label: e.target.value })}
                aria-label={`Step ${i + 1} name`} placeholder="Dilation"
                style={{ ...box, flex: '1 1 100%' }} />
              {!s.durationMin ? (
                <button type="button" className="lnk t-cap" onClick={() => setStep(i, { durationMin: 10 })}>+ wait</button>
              ) : null}
              {!s.ifUnavailable ? (
                <button type="button" className="lnk t-cap" onClick={() => setStep(i, { ifUnavailable: 'external' })}>+ if unavailable</button>
              ) : null}
              {s.note === null || s.note === undefined ? (
                <button type="button" className="lnk t-cap" onClick={() => setStep(i, { note: '' })}>+ note</button>
              ) : null}
              <button type="button" className="lnk t-cap" style={{ color: 'var(--danger-600)' }}
                onClick={() => { setOpen(null); setSteps((xs) => xs.filter((_, j) => j !== i)); }}>
                Remove this step
              </button>
            </div>
          ) : null}
        </Card>
      );
    })}

    {/* THE LINK STAYS UNDER THE STEPS — the board draws it there whatever
        else is open, and replacing it with the picker cost a click every time
        a second step was written. */}
    {adding ? (
      <Card>
        <p className="t-ovl mut">ADD A STEP</p>
        <InlineSelect value="" aria-label="Add a step from Items"
          onChange={(v) => (v === '__free' ? addFreeText() : addFromItem(v))}
          options={[
            { value: '', label: 'from Items…' },
            ...items.filter((i) => i.isActive !== false).map((i) => ({ value: i.id, label: `${i.name} · ${labels[i.assigneeRole]}` })),
            { value: '__free', label: 'free text' },
          ]} />
        {!items.length ? (
          <p className="t-cap mut mt8">
            No items yet — the Items tab writes the vocabulary, and free text works until it does.
          </p>
        ) : null}
      </Card>
    ) : null}
    <p className="t-sec w6 brand" style={{ margin: '10px 0', cursor: 'pointer' }} role="button" tabIndex={0}
      onClick={() => setAdding(true)}
      onKeyDown={(e) => e.key === 'Enter' && setAdding(true)}>
      + Add step — from Items, the catalog, a protocol, or free text
    </p>
    </>
  );
}

/**
 * NEW FLOW — `g_l11n.dc.html` (f11b-new): name, TRIGGERED FROM, steps in order.
 *
 * A MODAL, NOT THE PANE. The pane belongs to the flow that is SELECTED in the
 * list; writing a new one is a task, and the desk's rule puts a task in a
 * centred dialog (kit Modal). The list stays readable behind it, which is the
 * point — a new flow is usually written while looking at the ones that exist.
 *
 * TRIGGERED FROM is where a flow gets its one entrance. The encounter's Flow
 * button is built and is the default. Starting a flow automatically on every
 * booking of a type is NOT built — nothing watches bookings — so that chip is
 * drawn, inert and says so rather than promising a chain nobody will run.
 */
export function NewFlowModal({ open, items, onClose, onSaved }: {
  open: boolean;
  items: FlowItem[];
  onClose: () => void;
  onSaved: (t: FlowTemplate) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [steps, setSteps] = useState<FlowStepDef[]>([]);
  const [adding, setAdding] = useState(false);
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [trigger, setTrigger] = useState<BookingType | null>(null);
  const [saving, setSaving] = useState(false);
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const branches = useBranches().filter((b): b is typeof b & { id: string } => !!b.id);

  useEffect(() => {
    if (open) { setName(''); setSteps([]); setAdding(false); setOpenStep(null); setTrigger(null); setBranchIds([]); }
  }, [open]);

  const valid = name.trim().length >= 2 && steps.length > 0 && steps.every((s) => s.label.trim());

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const saved = await flowsApi.createTemplate({
        name: name.trim(),
        branchIds: branchIds.length ? branchIds : undefined,
        steps: steps.map((s) => ({
          ...s,
          label: s.label.trim(),
          durationMin: s.durationMin || null,
          note: s.note?.trim() || null,
        })),
        triggerBookingType: trigger,
      });
      await qc.invalidateQueries({ queryKey: ['flows'] });
      toast.success('Flow created');
      onSaved(saved);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The flow did not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width={820} phoneScreen title="New flow"
      sub="Doctors and the clinic manager write flows · executors execute them · every edit is logged"
      foot={
        <>
          <button type="button" className="btn btn-outline" disabled={saving} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || saving}
            onClick={() => void save()}>{saving ? 'Saving…' : 'Save flow'}</button>
        </>
      }>
      {/* Two columns on the desk; one on a phone (g_l16 stacks Name,
          Triggered from and the steps). */}
      <div className="kit-stack dr-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card>
            <p className="t-ovl mut">Name</p>
            {/* THE BOARD DRAWS A VALUE LINE WITH A PENCIL, not a bordered
                input: the card is 66px, and opening as a field made it 82 and
                the whole dialog 14px too tall. An empty one says "not set" and
                the pencil is the door. The placeholder it used to carry was
                the board's own sample flow name. */}
            <p className="t-strong mt8">
              <InlineEdit key={open ? 'open' : 'shut'} value={name} onCommit={setName}
                label="Flow name" />
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">Triggered from</p>
            <TriggerPicker value={trigger} onChange={setTrigger} />
          </Card>
          {branches.length > 1 ? (
            <Card>
              <p className="t-ovl mut">Runs at</p>
              <p className="mt8" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Chip kind={branchIds.length === 0 ? 'brand' : 'outline'} small
                  className={branchIds.length === 0 ? 'w6' : undefined}
                  onClick={() => setBranchIds([])}>
                  All branches
                </Chip>
                {branches.map((b) => {
                  const on = branchIds.includes(b.id);
                  return (
                    <Chip key={b.id} kind={on ? 'brand' : 'outline'} small
                      className={on ? 'w6' : undefined}
                      onClick={() => setBranchIds((cur) => (on ? cur.filter((x) => x !== b.id) : [...cur, b.id]))}>
                      {b.name}{on ? ' ✓' : ''}
                    </Chip>
                  );
                })}
              </p>
            </Card>
          ) : null}
          <Card style={{ borderColor: 'var(--stroke-200)' }}>
            <p className="t-sec" style={{ color: 'var(--ink-600)' }}>
              Executor is a ROLE, never a person. Whoever holds the role that day gets the card.
            </p>
          </Card>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="t-ovl mut" style={{ margin: '0 0 6px' }}>Steps — in order</p>
          <StepEditor steps={steps} setSteps={setSteps} items={items}
            adding={adding} setAdding={setAdding} open={openStep} setOpen={setOpenStep} />
          {!steps.length && !adding ? (
            <p className="t-cap mut">
              A flow is its steps. Add the first one — from Items, the catalog, a protocol, or free text.
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
